import { useCallback, useEffect, useMemo, useRef, useState, Fragment } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError, formatCurrency, formatDate } from "@/lib/api";
import { useAgency } from "@/context/AgencyContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Printer, Building2, FileDown, ChevronRight, ChevronDown, ChevronLeft, CheckCircle2, XCircle, Archive } from "lucide-react";
import { toast } from "sonner";
import { DatePicker } from "@/components/DatePicker";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const iso = (d) => {
  // 🆕 Usa i componenti LOCALI per evitare shift di fuso orario (toISOString converte in UTC
  //    e per date locali di mezzanotte italiana l'ISO diventa il giorno precedente,
  //    facendo tornare indietro il navigatore "Periodo in visione").
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const previousMonthRange = () => {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const end = new Date(now.getFullYear(), now.getMonth(), 0); // last day of previous month
  return { start: iso(start), end: iso(end) };
};

const currentMonthRange = () => {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  return { start: iso(start), end: iso(end) };
};

const currentYearRange = () => {
  const now = new Date();
  return { start: `${now.getFullYear()}-01-01`, end: iso(now) };
};

const isInstPending = (r) => !r.commission_collection || (r.fee > 0 && !r.fee_collection);
const isPolicyPending = (p) => (p.installments_in_range || []).some(isInstPending);

export default function CommissionsStatement() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { agency } = useAgency();
  const focusId = searchParams.get("focus");
  const defaultRange = currentMonthRange();
  const [startDate, setStartDate] = useState(defaultRange.start);
  const [endDate, setEndDate] = useState(defaultRange.end);
  const [groupBy, setGroupBy] = useState("company");
  const [companyFilter, setCompanyFilter] = useState("all");
  const [brokerFilter, setBrokerFilter] = useState("all");
  const [companies, setCompanies] = useState([]);
  const [brokers, setBrokers] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [expanded, setExpanded] = useState({}); // { policy_id: bool } → rate espanse per polizza
  // 🆕 Provvigioni Incassate: selezione multipla + filtro stato
  const [selectedInstallments, setSelectedInstallments] = useState({}); // { "policy_id::installment_date": true }
  const [collectionStatusFilter, setCollectionStatusFilter] = useState("all"); // all | uncollected | collected
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [bulkCollectedAt, setBulkCollectedAt] = useState(() => iso(new Date()));
  const [bulkBusy, setBulkBusy] = useState(false);
  const [singleCollectDialog, setSingleCollectDialog] = useState(null); // { policy_id, installment_date, amount_default, current }
  const [singleCollectedAt, setSingleCollectedAt] = useState(() => iso(new Date()));
  const [singleAmount, setSingleAmount] = useState("");
  const [singleNote, setSingleNote] = useState("");
  const [singleBusy, setSingleBusy] = useState(false);
  // 🆕 Vista compatta/dettagliata: nasconde le colonne "Diritti ag." e "Diritti brk." se compatta.
  // Persistenza in localStorage; default: dettagliata.
  const [detailedView, setDetailedView] = useState(() => {
    try {
      const v = localStorage.getItem("statement_detailed_view");
      return v == null ? true : v === "true";
    } catch { return true; }
  });
  useEffect(() => {
    try { localStorage.setItem("statement_detailed_view", String(detailedView)); } catch { /* ignore */ }
  }, [detailedView]);
  // 🆕 Durante l'export PDF forziamo vista compatta (senza toccare la preferenza utente)
  const [printCompact, setPrintCompact] = useState(false);
  const showDetail = detailedView && !printCompact;
  const printRef = useRef(null);

  // Load companies + brokers once for the filter dropdowns
  useEffect(() => {
    api.get("/companies").then(({ data }) => setCompanies(data || [])).catch(() => {});
    api.get("/brokers").then(({ data }) => setBrokers(data || [])).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { start_date: startDate, end_date: endDate, group_by: groupBy };
      if (companyFilter !== "all") params.company_id = companyFilter;
      if (brokerFilter !== "all") params.broker_id = brokerFilter;
      const { data } = await api.get("/commissions/statement", { params });
      setData(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [startDate, endDate, groupBy, companyFilter, brokerFilter]);

  useEffect(() => { load(); }, [load]);

  // 🆕 Toggle selezione singola rata (chiave = policy_id::installment_date)
  const toggleInstallmentSelection = useCallback((policyId, installmentDate, paidDate = null) => {
    const key = `${policyId}::${installmentDate}`;
    setSelectedInstallments((s) => {
      const next = { ...s };
      if (next[key]) delete next[key];
      else next[key] = { policy_id: policyId, installment_date: installmentDate, paid_date: paidDate };
      return next;
    });
  }, []);

  const selectedItems = useMemo(() => Object.values(selectedInstallments), [selectedInstallments]);

  const clearSelection = useCallback(() => setSelectedInstallments({}), []);

  const openSingleCollectDialog = useCallback((policyId, installmentDate, amountDefault, current) => {
    setSingleCollectDialog({ policy_id: policyId, installment_date: installmentDate, amount_default: amountDefault, current });
    setSingleCollectedAt(current?.collected_at || iso(new Date()));
    setSingleAmount(String(current?.amount_collected ?? amountDefault ?? "0"));
    setSingleNote(current?.note || "");
  }, []);

  const confirmSingleCollect = useCallback(async () => {
    if (!singleCollectDialog) return;
    setSingleBusy(true);
    try {
      const { policy_id, installment_date } = singleCollectDialog;
      await api.post(`/policies/${policy_id}/installments/${installment_date}/collect-commission`, {
        collected_at: singleCollectedAt,
        amount_collected: parseFloat(singleAmount) || 0,
        note: singleNote || null,
      });
      toast.success("Provvigione segnata come incassata.");
      setSingleCollectDialog(null);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSingleBusy(false);
    }
  }, [singleCollectDialog, singleCollectedAt, singleAmount, singleNote, load]);

  const uncollectSingle = useCallback(async (policyId, installmentDate) => {
    if (!window.confirm("Annullare l'incasso di questa provvigione? La rata tornerà a stato \"Da incassare\".")) return;
    try {
      await api.post(`/policies/${policyId}/installments/${installmentDate}/uncollect-commission`);
      toast.success("Incasso annullato.");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, [load]);

  const toggleFeeCollection = useCallback(async (policyId, r) => {
    try {
      if (r.fee_collection) {
        if (!window.confirm("Annullare l'incasso della fee di questa rata?")) return;
        await api.post(`/policies/${policyId}/installments/${r.due_date}/uncollect-fee`);
        toast.success("Incasso fee annullato.");
      } else {
        await api.post(`/policies/${policyId}/installments/${r.due_date}/collect-fee`, { collected_at: iso(new Date()), amount_collected: r.fee || 0 });
        toast.success("Fee segnata come incassata.");
      }
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, [load]);

  const confirmBulkCollect = useCallback(async () => {
    if (selectedItems.length === 0) return;
    setBulkBusy(true);
    try {
      const { data: res } = await api.post("/commissions/collect-bulk", {
        items: selectedItems.map((it) => ({
          policy_id: it.policy_id,
          installment_date: it.installment_date,
          // 🆕 20/07/2026 — Data incasso = data pagamento effettivo se disponibile
          collected_at: it.paid_date || undefined,
        })),
        collected_at: bulkCollectedAt,
      });
      const okCount = res.collected_count || 0;
      const errCount = (res.errors || []).length;
      if (okCount > 0 && errCount === 0) toast.success(`${okCount} provvigion${okCount === 1 ? "e segnata" : "i segnate"} come incassate.`);
      else if (okCount > 0 && errCount > 0) toast.warning(`${okCount} incassate, ${errCount} errori.`);
      else toast.error(`Nessuna incassata. ${errCount} errori.`);
      setBulkDialogOpen(false);
      clearSelection();
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBulkBusy(false);
    }
  }, [selectedItems, bulkCollectedAt, load, clearSelection]);

  // 🆕 20/07/2026 — Incasso rapido "1-click" per polizza:
  // segna TUTTE le rate rimanenti di una polizza come incassate con data odierna,
  // con aggiornamento ottimistico immediato del badge (0/1 → 1/1) e delle righe.
  const quickCollectPolicy = useCallback(async (policy) => {
    const uncollected = (policy.installments_in_range || []).filter((r) => !r.commission_collection);
    if (uncollected.length === 0) {
      toast.info("Tutte le rate di questa polizza sono già incassate.");
      return;
    }
    const today = iso(new Date());
    // Deseleziona eventuali rate di questa polizza già selezionate
    setSelectedInstallments((s) => {
      const next = { ...s };
      for (const r of uncollected) delete next[`${policy.id}::${r.due_date}`];
      return next;
    });
    // Optimistic update: applica commission_collection alle rate localmente
    const snapshot = JSON.stringify(data);
    setData((d) => {
      if (!d) return d;
      const clone = JSON.parse(JSON.stringify(d));
      for (const g of (clone.groups || [])) {
        for (const p of (g.policies || [])) {
          if (p.id !== policy.id) continue;
          for (const r of (p.installments_in_range || [])) {
            if (!r.commission_collection) {
              r.commission_collection = {
                collected_at: today,
                amount_collected: r.commission || 0,
                _optimistic: true,
              };
            }
          }
        }
      }
      return clone;
    });
    try {
      const { data: res } = await api.post("/commissions/collect-bulk", {
        items: uncollected.map((r) => ({
          policy_id: policy.id,
          installment_date: r.due_date,
          // 🆕 20/07/2026 — Ogni rata usa la SUA data pagamento effettivo come data incasso provvigione
          collected_at: r.paid_date || undefined,
        })),
        collected_at: today,
      });
      const okCount = res.collected_count || 0;
      const errCount = (res.errors || []).length;
      if (okCount > 0 && errCount === 0) {
        toast.success(`${okCount} rat${okCount === 1 ? "a incassata" : "e incassate"} · ${policy.client_name || policy.policy_number}`);
      } else if (okCount > 0) {
        toast.warning(`${okCount} incassate, ${errCount} errori.`);
      } else {
        toast.error(`Nessuna incassata. ${errCount} errori.`);
      }
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
      // Rollback optimistic update
      try { setData(JSON.parse(snapshot)); } catch { await load(); }
    }
  }, [data, load]);

  // 🆕 20/07/2026 — Toggle selezione TUTTE le rate rimanenti di una polizza (per bulk)
  const toggleRowSelection = useCallback((policy) => {
    const uncollected = (policy.installments_in_range || []).filter((r) => !r.commission_collection);
    if (uncollected.length === 0) return;
    setSelectedInstallments((s) => {
      const next = { ...s };
      const allSelected = uncollected.every((r) => next[`${policy.id}::${r.due_date}`]);
      for (const r of uncollected) {
        const key = `${policy.id}::${r.due_date}`;
        if (allSelected) delete next[key];
        else next[key] = { policy_id: policy.id, installment_date: r.due_date, paid_date: r.paid_date || null };
      }
      return next;
    });
  }, []);

  // 🆕 20/07/2026 — Incasso "tutto il periodo": marca TUTTE le rate uncollected visibili nell'estratto conto.
  const [collectAllOpen, setCollectAllOpen] = useState(false);
  const [collectAllBusy, setCollectAllBusy] = useState(false);
  const allUncollectedItems = useMemo(() => {
    if (!data) return [];
    const items = [];
    for (const g of (data.groups || [])) {
      for (const p of (g.policies || [])) {
        for (const r of (p.installments_in_range || [])) {
          if (!r.commission_collection) items.push({
            policy_id: p.id,
            installment_date: r.due_date,
            // 🆕 20/07/2026 — Data incasso = data pagamento effettivo della rata
            collected_at: r.paid_date || undefined,
          });
        }
      }
    }
    return items;
  }, [data]);
  const confirmCollectAll = useCallback(async () => {
    if (allUncollectedItems.length === 0) return;
    setCollectAllBusy(true);
    try {
      const today = iso(new Date());
      const { data: res } = await api.post("/commissions/collect-bulk", { items: allUncollectedItems, collected_at: today });
      const okCount = res.collected_count || 0;
      const errCount = (res.errors || []).length;
      if (okCount > 0 && errCount === 0) toast.success(`${okCount} rat${okCount === 1 ? "a incassata" : "e incassate"} in un colpo solo.`);
      else if (okCount > 0) toast.warning(`${okCount} incassate, ${errCount} errori.`);
      else toast.error(`Nessuna incassata. ${errCount} errori.`);
      setCollectAllOpen(false);
      clearSelection();
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setCollectAllBusy(false);
    }
  }, [allUncollectedItems, clearSelection, load]);

  // Scroll/highlight when arriving with ?focus=<policy_id>
  useEffect(() => {
    if (!focusId || !data) return;
    const has = (data.groups || []).some((g) => (g.policies || []).some((p) => p.id === focusId));
    if (!has) {
      // Allarga il range se la polizza non è inclusa nel periodo attuale
      setStartDate("2020-01-01");
      setEndDate("2099-12-31");
      return;
    }
    const el = document.querySelector(`[data-testid="statement-row-${focusId}"]`);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.classList.add("ring-2", "ring-indigo-400", "bg-indigo-50/60");
      setTimeout(() => {
        el.classList.remove("ring-2", "ring-indigo-400", "bg-indigo-50/60");
        // pulisce param dopo l'highlight
        const next = new URLSearchParams(searchParams);
        next.delete("focus");
        setSearchParams(next, { replace: true });
      }, 2500);
    }
  }, [focusId, data, searchParams, setSearchParams]);

  const periodLabel = useMemo(() => {
    if (!startDate && !endDate) return "Periodo completo";
    return `${formatDate(startDate)} → ${formatDate(endDate)}`;
  }, [startDate, endDate]);

  const setPreviousMonth = () => { const r = previousMonthRange(); setStartDate(r.start); setEndDate(r.end); };
  const setCurrentMonth = () => { const r = currentMonthRange(); setStartDate(r.start); setEndDate(r.end); };
  const setThisYear = () => { const r = currentYearRange(); setStartDate(r.start); setEndDate(r.end); };
  // 🆕 Naviga al mese precedente/successivo mantenendo il range 1°→ultimo giorno del mese
  const shiftMonth = (delta) => {
    // Usa startDate come "ancora" per capire il mese corrente in view
    const ref = startDate ? new Date(startDate) : new Date();
    const target = new Date(ref.getFullYear(), ref.getMonth() + delta, 1);
    const start = new Date(target.getFullYear(), target.getMonth(), 1);
    const end = new Date(target.getFullYear(), target.getMonth() + 1, 0);
    setStartDate(iso(start)); setEndDate(iso(end));
  };
  // 🆕 Cambia anno mantenendo il mese corrente in view
  const setYear = (year) => {
    const ref = startDate ? new Date(startDate) : new Date();
    const monthIdx = ref.getMonth();
    const start = new Date(Number(year), monthIdx, 1);
    const end = new Date(Number(year), monthIdx + 1, 0);
    setStartDate(iso(start)); setEndDate(iso(end));
  };
  // 🆕 Anno corrente in view + lista anni (2020 → anno corrente + 1)
  const currentYearInView = useMemo(() => {
    if (!startDate) return new Date().getFullYear();
    return new Date(startDate).getFullYear();
  }, [startDate]);
  const availableYears = useMemo(() => {
    const cur = new Date().getFullYear();
    const arr = [];
    for (let y = 2020; y <= cur + 1; y++) arr.push(y);
    return arr;
  }, []);
  // 🆕 Label del mese corrente in view (es. "Gennaio 2026")
  const currentMonthLabel = useMemo(() => {
    if (!startDate) return "";
    const d = new Date(startDate);
    return d.toLocaleDateString("it-IT", { month: "long", year: "numeric" });
  }, [startDate]);

  const exportPdf = async () => {
    if (!printRef.current || !data) return;
    setExporting(true);
    // 🆕 Forza vista compatta durante l'export (evita colonne troppo strette su A4)
    setPrintCompact(true);
    // Wait for re-render after state change
    await new Promise((r) => setTimeout(r, 50));
    try {
      const { default: html2pdf } = await import("html2pdf.js");
      const agencyName = (agency?.name || "estratto-conto").replace(/[^A-Za-z0-9_-]+/g, "_").toLowerCase();
      const filename = `estratto_${agencyName}_${startDate}_${endDate}.pdf`;
      // Apply compact-print class temporarily to shrink fonts/paddings during export
      printRef.current.classList.add("pdf-compact");
      // 🆕 Se ci sono molte colonne (>=10) applica anche pdf-dense per compattare ulteriormente
      const colCount = printRef.current.querySelector("table thead tr")?.children?.length || 0;
      if (colCount >= 10) printRef.current.classList.add("pdf-dense");
      try {
        await html2pdf().set({
          margin: [6, 6, 6, 6],
          filename,
          image: { type: "jpeg", quality: 0.95 },
          html2canvas: { scale: 2, useCORS: true, backgroundColor: "#ffffff" },
          jsPDF: { unit: "mm", format: "a4", orientation: "landscape" },
          pagebreak: { mode: ["css", "legacy"] },
        }).from(printRef.current).save();
      } finally {
        printRef.current.classList.remove("pdf-compact");
        printRef.current.classList.remove("pdf-dense");
      }
      toast.success("PDF generato");
    } catch (e) {
      toast.error(`Errore export PDF: ${e?.message || e}`);
    } finally {
      setPrintCompact(false);
      setExporting(false);
    }
  };

  return (
    <div className="space-y-6 animate-fade-up" data-testid="commissions-statement-page">
      <header className="flex items-center justify-between flex-wrap gap-4 print:hidden">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-blue-600 mb-2">Provvigioni</p>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">Estratto conto</h1>
          <p className="text-slate-500 mt-1">Riepilogo provvigioni per periodo · raggruppato per {groupBy === "company" ? "compagnia" : "broker"}</p>
        </div>
        <div className="flex gap-2 items-center">
          <div className="hidden sm:inline-flex rounded-md border border-slate-300 bg-white shadow-sm overflow-hidden" data-testid="view-mode-toggle" role="group" aria-label="Vista tabella">
            <button
              type="button"
              onClick={() => setDetailedView(false)}
              className={`px-3 py-1.5 text-xs font-medium transition-colors ${!detailedView ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}
              data-testid="view-compact"
              title="Nasconde le colonne Diritti ag./brk. — vista compatta"
            >
              Vista compatta
            </button>
            <button
              type="button"
              onClick={() => setDetailedView(true)}
              className={`px-3 py-1.5 text-xs font-medium transition-colors ${detailedView ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}
              data-testid="view-detailed"
              title="Mostra anche le colonne Diritti ag./brk. — vista dettagliata"
            >
              Vista dettagliata
            </button>
          </div>
          <Button data-testid="export-pdf" onClick={exportPdf} disabled={exporting || !data} variant="outline">
            <FileDown className="h-4 w-4 mr-2" /> {exporting ? "Esporto…" : "Esporta PDF"}
          </Button>
          <Button
            data-testid="open-collected-archive"
            onClick={() => navigate("/commissions/collected")}
            variant="outline"
            className="border-emerald-300 text-emerald-800 hover:bg-emerald-50"
            title="Archivio Provvigioni Incassate"
          >
            <Archive className="h-4 w-4 mr-2" /> Archivio incassi
          </Button>
          <Button data-testid="print-statement" onClick={() => window.print()} className="bg-[#0F172A] hover:bg-[#1E293B]">
            <Printer className="h-4 w-4 mr-2" /> Stampa estratto
          </Button>
        </div>
      </header>

      <div className="bg-white border border-slate-200 rounded-lg p-4 print:hidden space-y-3">
        {/* 🆕 Navigazione mese ← → con label centrata + selettore anno */}
        <div className="flex items-center justify-between gap-2 bg-slate-50 border border-slate-200 rounded-md px-2 py-1.5">
          <Button
            variant="outline"
            size="sm"
            data-testid="month-prev"
            onClick={() => shiftMonth(-1)}
            className="border-slate-300"
            title="Mese precedente"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <div className="flex-1 flex items-center justify-center gap-3">
            <div className="text-center">
              <p className="text-xs uppercase tracking-[0.15em] text-slate-500">Periodo in visione</p>
              <p className="font-display text-base font-semibold text-slate-900 capitalize tabular-nums" data-testid="month-label">{currentMonthLabel}</p>
            </div>
            <Select value={String(currentYearInView)} onValueChange={setYear}>
              <SelectTrigger data-testid="year-select" className="h-8 w-[100px] bg-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {availableYears.slice().reverse().map((y) => (
                  <SelectItem key={y} value={String(y)} data-testid={`year-${y}`}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button
            variant="outline"
            size="sm"
            data-testid="month-next"
            onClick={() => shiftMonth(1)}
            className="border-slate-300"
            title="Mese successivo"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
          <div><Label>Da</Label><DatePicker data-testid="filter-start" value={startDate} onChange={(e) => setStartDate(e.target.value)} /></div>
          <div><Label>A</Label><DatePicker data-testid="filter-end" value={endDate} onChange={(e) => setEndDate(e.target.value)} /></div>
          <div>
            <Label>Raggruppa per</Label>
            <Select value={groupBy} onValueChange={setGroupBy}>
              <SelectTrigger data-testid="filter-group"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="company">Compagnia</SelectItem>
                <SelectItem value="broker">Broker / Agenzia</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-end gap-1.5">
            <Button variant="outline" data-testid="quick-prev-month" onClick={setPreviousMonth} className="flex-1 text-xs">Mese prec.</Button>
            <Button variant="outline" data-testid="quick-this-month" onClick={setCurrentMonth} className="flex-1 text-xs">Questo mese</Button>
            <Button variant="outline" data-testid="quick-this-year" onClick={setThisYear} className="flex-1 text-xs">Anno</Button>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2 border-t border-slate-100">
          <div>
            <Label>Compagnia <span className="text-xs text-slate-400 font-normal">(facoltativo)</span></Label>
            <Select value={companyFilter} onValueChange={setCompanyFilter}>
              <SelectTrigger data-testid="statement-filter-company"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutte le compagnie</SelectItem>
                {companies.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Broker / Agenzia <span className="text-xs text-slate-400 font-normal">(facoltativo)</span></Label>
            <Select value={brokerFilter} onValueChange={setBrokerFilter}>
              <SelectTrigger data-testid="statement-filter-broker"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti i broker / agenzie</SelectItem>
                <SelectItem value="__none__">— Senza broker —</SelectItem>
                {brokers.map((b) => (
                  <SelectItem key={b.id} value={b.id}>{b.name} ({b.type})</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Print-friendly content */}
      <div ref={printRef} className="bg-white border border-slate-200 rounded-lg p-8 print:border-none print:shadow-none">
        {/* Print header with agency */}
        <div className="flex items-center justify-between border-b border-slate-200 pb-4 mb-6">
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-md bg-blue-600 flex items-center justify-center overflow-hidden">
              {agency?.logo_url ? <img src={agency.logo_url} alt="logo" className="h-full w-full object-cover" /> : <Building2 className="h-6 w-6 text-white" />}
            </div>
            <div>
              <h2 className="font-display text-xl font-semibold">{agency?.legal_name || agency?.name || "Assicura"}</h2>
              <p className="text-xs text-slate-500">{agency?.address || ""} {agency?.city ? `· ${agency.city}` : ""} {agency?.cap ? `(${agency.cap})` : ""}</p>
              {agency?.vat_number && <p className="text-xs text-slate-500">P.IVA {agency.vat_number} · RUI {agency.rui_number || "—"}</p>}
            </div>
          </div>
          <div className="text-right">
            <p className="text-xs uppercase tracking-wide text-slate-500">Estratto conto provvigioni</p>
            <p className="text-sm font-medium">{periodLabel}</p>
            {(companyFilter !== "all" || brokerFilter !== "all") && (
              <p className="text-[11px] text-blue-600 mt-1">
                {companyFilter !== "all" && <>Compagnia: <strong>{companies.find((c) => c.id === companyFilter)?.name || "—"}</strong> </>}
                {brokerFilter !== "all" && (
                  <>Broker: <strong>{brokerFilter === "__none__" ? "(senza broker)" : (brokers.find((b) => b.id === brokerFilter)?.name || "—")}</strong></>
                )}
              </p>
            )}
            <p className="text-[10px] text-slate-400">Stampato il {new Date().toLocaleDateString("it-IT")}</p>
          </div>
        </div>

        {loading && <p className="text-center py-12 text-slate-400">Caricamento…</p>}

        {data && (() => {
          const all = (data.groups || []).flatMap((g) => g.policies || []);
          if (all.length === 0 || all.some(isPolicyPending)) return null;
          return (
            <div className="mb-4 flex items-center gap-3 rounded-md border-2 border-emerald-500 bg-emerald-600 text-white px-4 py-3" data-testid="statement-all-collected-banner">
              <CheckCircle2 className="h-6 w-6 shrink-0" />
              <div>
                <p className="font-display text-lg font-bold tracking-wide">TUTTO INCASSATO</p>
                <p className="text-xs text-emerald-50">Provvigioni e fee di tutte le polizze del periodo risultano incassate: nessun importo in sospeso.</p>
              </div>
            </div>
          );
        })()}

        {/* 🆕 Toolbar Incassi Provvigioni */}
        {data && (
          <div className="print:hidden flex items-center justify-between gap-2 flex-wrap mb-4 p-3 rounded-md border border-emerald-200 bg-emerald-50/40" data-testid="collections-toolbar">
            <div className="flex items-center gap-2">
              <span className="text-xs uppercase tracking-wide text-emerald-800 font-semibold">Provvigioni:</span>
              <div className="inline-flex rounded-md border border-emerald-300 bg-white overflow-hidden">
                <button
                  type="button"
                  data-testid="filter-all"
                  onClick={() => setCollectionStatusFilter("all")}
                  className={`px-2.5 py-1 text-xs font-medium ${collectionStatusFilter === "all" ? "bg-emerald-600 text-white" : "text-emerald-800 hover:bg-emerald-50"}`}
                >
                  Tutte
                </button>
                <button
                  type="button"
                  data-testid="filter-uncollected"
                  onClick={() => setCollectionStatusFilter("uncollected")}
                  className={`px-2.5 py-1 text-xs font-medium border-l border-emerald-300 ${collectionStatusFilter === "uncollected" ? "bg-amber-500 text-white" : "text-amber-800 hover:bg-amber-50"}`}
                >
                  Da incassare
                </button>
                <button
                  type="button"
                  data-testid="filter-collected"
                  onClick={() => setCollectionStatusFilter("collected")}
                  className={`px-2.5 py-1 text-xs font-medium border-l border-emerald-300 ${collectionStatusFilter === "collected" ? "bg-emerald-600 text-white" : "text-emerald-800 hover:bg-emerald-50"}`}
                >
                  Incassate
                </button>
              </div>
              <span className="text-xs text-slate-500 italic ml-2 hidden sm:inline">Espandi una polizza per vedere le rate.</span>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {/* 🆕 20/07/2026 — Incassa tutto il periodo con un solo click */}
              {allUncollectedItems.length > 0 && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setCollectAllOpen(true)}
                  className="h-8 border-emerald-500 text-emerald-800 hover:bg-emerald-50 font-semibold"
                  data-testid="collect-all-period-btn"
                  title={`Incassa TUTTE le ${allUncollectedItems.length} rate rimanenti del periodo (data odierna)`}
                >
                  <CheckCircle2 className="h-4 w-4 mr-1" /> Incassa tutto il periodo ({allUncollectedItems.length})
                </Button>
              )}
              {selectedItems.length > 0 && (
                <div className="flex items-center gap-2 bg-white border border-emerald-400 rounded-md px-3 py-1.5 shadow-sm" data-testid="bulk-toolbar">
                  <span className="text-xs font-semibold text-emerald-900 tabular-nums">
                    {selectedItems.length} rat{selectedItems.length === 1 ? "a" : "e"} selezionat{selectedItems.length === 1 ? "a" : "e"}
                  </span>
                  <Button
                    size="sm"
                    data-testid="bulk-collect-btn"
                    onClick={() => { setBulkCollectedAt(iso(new Date())); setBulkDialogOpen(true); }}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white h-8 font-semibold"
                  >
                    <CheckCircle2 className="h-4 w-4 mr-1" /> Incassa Selezionati
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={clearSelection}
                    className="border-slate-300 h-8"
                    data-testid="bulk-clear-btn"
                  >
                    Annulla selezione
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}

        {data && (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3 mb-6">
              <div className="bg-slate-50 rounded-md p-3">
                <p className="text-[10px] uppercase tracking-wide text-slate-500">Polizze</p>
                <p className="text-2xl font-display font-semibold tabular-nums">{data.totals.count}</p>
              </div>
              <div className="bg-slate-50 rounded-md p-3">
                <p className="text-[10px] uppercase tracking-wide text-slate-500">Premi Lordi</p>
                <p className="text-xl font-display font-semibold tabular-nums">{formatCurrency(data.totals.premium)}</p>
              </div>
              <div className="bg-slate-50 rounded-md p-3">
                <p className="text-[10px] uppercase tracking-wide text-slate-500">Premi Netti</p>
                <p className="text-xl font-display font-semibold tabular-nums">{formatCurrency(data.totals.premium_net || 0)}</p>
              </div>
              <div className="bg-emerald-50 rounded-md p-3">
                <p className="text-[10px] uppercase tracking-wide text-emerald-700">Provvigioni</p>
                <p className="text-xl font-display font-semibold tabular-nums text-emerald-800">{formatCurrency(data.totals.commission)}</p>
              </div>
              <div className="bg-sky-50 rounded-md p-3">
                <p className="text-[10px] uppercase tracking-wide text-sky-700">Fee broker/Ag.</p>
                <p className="text-xl font-display font-semibold tabular-nums text-sky-800">{formatCurrency(data.totals.cost)}</p>
              </div>
              <div className="bg-amber-50 rounded-md p-3" data-testid="kpi-partner-net">
                <p className="text-[10px] uppercase tracking-wide text-amber-700">Spett. broker</p>
                <p className="text-xl font-display font-semibold tabular-nums text-amber-800">{formatCurrency(data.totals.partner_net || 0)}</p>
              </div>
              {data.totals.broker_fixed_fee > 0 && (
                <div className="bg-purple-50 rounded-md p-3" data-testid="kpi-broker-fixed-fee">
                  <p className="text-[10px] uppercase tracking-wide text-purple-700" title="Compenso fisso pattuito col broker/agenzia">Comp. fisso</p>
                  <p className="text-xl font-display font-semibold tabular-nums text-purple-800">{formatCurrency(data.totals.broker_fixed_fee)}</p>
                </div>
              )}
              <div className="bg-indigo-50 rounded-md p-3" data-testid="kpi-agency-net">
                <p className="text-[10px] uppercase tracking-wide text-indigo-700">Quota agenzia tot.</p>
                <p className="text-xl font-display font-semibold tabular-nums text-indigo-800">{formatCurrency(data.totals.agency_total || data.totals.agency_net || 0)}</p>
                {data.totals.extra_fees > 0 && (
                  <p className="text-[9px] text-indigo-600 mt-0.5">incl. extra {formatCurrency(data.totals.extra_fees)}</p>
                )}
              </div>
              {/* 🆕 KPI Incassi provvigioni */}
              {(() => {
                let totInst = 0, totCollected = 0, totAmt = 0;
                (data.groups || []).forEach((g) => (g.policies || []).forEach((p) => {
                  (p.installments_in_range || []).forEach((r) => {
                    totInst++;
                    if (r.commission_collection) { totCollected++; totAmt += r.commission_collection.amount_collected || 0; }
                  });
                }));
                if (totInst === 0) return null;
                const pct = totInst > 0 ? Math.round((totCollected / totInst) * 100) : 0;
                const bg = totCollected === totInst ? "bg-emerald-50" : totCollected > 0 ? "bg-amber-50" : "bg-red-50";
                const fg = totCollected === totInst ? "text-emerald-800" : totCollected > 0 ? "text-amber-900" : "text-red-800";
                return (
                  <div className={`${bg} rounded-md p-3 col-span-2 lg:col-span-1`} data-testid="kpi-collected">
                    <p className={`text-[10px] uppercase tracking-wide ${fg}`}>Incassi provv.</p>
                    <p className={`text-xl font-display font-semibold tabular-nums ${fg}`}>{formatCurrency(totAmt)}</p>
                    <p className={`text-[9px] ${fg} mt-0.5`}>{totCollected}/{totInst} rate ({pct}%)</p>
                  </div>
                );
              })()}
            </div>

            {/* 🆕 Avviso backfill: elenca le polizze in cui almeno una rata usa il paid_date polizza come default */}
            {(() => {
              const backfillPolicies = (data.groups || [])
                .flatMap((g) => (g.policies || []).map((p) => ({ ...p, _groupName: g.name })))
                .filter((p) => p.paid_date_backfilled);
              if (backfillPolicies.length === 0) return null;
              return (
                <div
                  data-testid="statement-backfill-banner"
                  className="mb-5 rounded-lg border border-amber-300 bg-gradient-to-r from-amber-50 to-yellow-50 p-3 sm:p-4 shadow-sm"
                >
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-amber-900 flex items-center gap-1.5">
                        ⚠ {backfillPolicies.length} {backfillPolicies.length === 1 ? "polizza usa" : "polizze usano"} il backfill automatico
                      </p>
                      <p className="text-[11px] text-amber-800 mt-0.5">
                        L&apos;Estratto ha propagato il <em>paid_date</em> polizza come &quot;Data pagam. effettivo&quot; per rate non compilate. Correggi le date rate per un calcolo più preciso.
                      </p>
                    </div>
                    <span className="text-[10px] text-amber-700 shrink-0">Clicca un numero polizza per aprirla ▶</span>
                  </div>
                  <div className="mt-2.5 flex flex-wrap gap-1.5">
                    {backfillPolicies.map((p) => (
                      <button
                        key={p.id}
                        type="button"
                        data-testid={`backfill-fix-${p.id}`}
                        onClick={() => {
                          const returnTo = `/commissions/statement${window.location.search || ""}`;
                          navigate(`/policies?focus=${p.id}&return_to=${encodeURIComponent(returnTo)}`);
                        }}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-amber-400 bg-white hover:bg-amber-100 text-[11px] font-mono font-semibold text-amber-900 shadow-sm transition-colors"
                        title={`${p.client_name || ""} · ${p._groupName} — apri per correggere le date rate`}
                      >
                        {p.policy_number || "—"}
                      </button>
                    ))}
                  </div>
                </div>
              );
            })()}

            {data.groups.length === 0 && (
              <p className="text-center py-12 text-slate-400">Nessuna polizza nel periodo selezionato.</p>
            )}

            {data.groups.map((g) => {
              // 🆕 20/07/2026 — Applica il filtro di stato incasso alle righe della tabella
              const filteredPolicies = (g.policies || []).filter((p) => {
                if (collectionStatusFilter === "all") return true;
                const inst = p.installments_in_range || [];
                if (inst.length === 0) return collectionStatusFilter === "all";
                if (collectionStatusFilter === "collected") return inst.every((r) => !!r.commission_collection);
                if (collectionStatusFilter === "uncollected") return inst.some((r) => !r.commission_collection);
                return true;
              });
              if (filteredPolicies.length === 0) return null;
              // 🆕 Feb 2026 — Colore identificativo del broker se il group_by è "broker"
              const groupBroker = groupBy === "broker" ? brokers.find((b) => b.id === g.key) : null;
              const groupColor = groupBroker?.color || null;
              return (
              <div key={g.key} className="mb-8" data-testid={`group-${g.key}`}>
                <div className="flex items-center justify-between border-b-2 border-blue-600 pb-2 mb-3" style={groupColor ? { borderBottomColor: groupColor } : undefined}>
                  <h3 className="font-display text-lg font-semibold text-blue-900 inline-flex items-center gap-2">
                    {groupColor && (
                      <span className="inline-block w-3 h-3 rounded-full border border-slate-200 shrink-0" style={{ backgroundColor: groupColor }} title={`Colore identificativo broker: ${groupColor}`} />
                    )}
                    {g.name}
                  </h3>
                  <span className="text-xs text-slate-500">
                    {collectionStatusFilter !== "all" && filteredPolicies.length !== g.totals.count
                      ? `${filteredPolicies.length} / ${g.totals.count} polizze`
                      : `${g.totals.count} polizze`}
                  </span>
                </div>
                <table className="w-full text-sm">
                  <thead className="text-xs uppercase tracking-wide text-slate-500 border-b border-slate-200">
                    <tr>
                      <th className="text-left py-2">N° pol</th>
                      <th className="text-left py-2">Cliente</th>
                      <th className="text-left py-2">Tipo</th>
                      <th className="text-left py-2">Data pagam.</th>
                      <th className="text-left py-2">{groupBy === "company" ? "Broker / Agenzia" : "Compagnia"}</th>
                      <th className="text-right py-2">Premio Lordo</th>
                      <th className="text-right py-2">Premio Netto</th>
                      <th className="text-right py-2" title="Fee Broker (rip.) — importo pattuito col broker/agenzia partner da ripartire secondo la % accordata">Fee Broker (rip.) tot.</th>
                      <th className="text-right py-2" title="Spese di gestione broker/agenzia (100% al broker/agenzia)">Spese Gest. tot.</th>
                      <th className="text-right py-2" title="Compenso fisso pattuito col broker/agenzia">Comp. Fisso tot.</th>
                      <th className="text-right py-2" title="Extra fees (importi accessori)">Extra Fees tot.</th>
                      <th className="text-right py-2" title="Quota della Fee Broker (rip.) che resta alla tua agenzia">Tot. Fee Agenzia</th>
                      <th className="text-right py-2" title="Margine automatico = Prezzo vendita − Prezzo acquisto prodotti">Margine (auto)</th>
                      <th className="text-right py-2" title="Bonus Multi-Garanzia: attivo quando il broker ha una % bonus configurata e la polizza contiene più di una garanzia con premio netto valorizzato. Il valore mostrato sostituisce la provvigione standard.">Multi-Garanzia</th>
                      <th className="text-right py-2" title="Provv. Ag. (fee incl.) = Provvigione Agenzia + Fee Broker (rip.) × %agenzia + Extra + Comp. Fisso">Provv. Ag. (fee incl.)</th>
                      <th className="text-center py-2" title="Stato incassi provvigioni (rate incassate / totali)">Incassi</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredPolicies.map((p) => {
                      const hasInst = Array.isArray(p.installments_in_range) && p.installments_in_range.length > 0;
                      const isOpen = !!expanded[p.id];
                      return (
                      <Fragment key={p.id}>
                      <tr
                        className={`border-b border-slate-100 cursor-pointer transition-colors ${isPolicyPending(p) ? "bg-yellow-50 hover:bg-yellow-100 shadow-[inset_4px_0_0_#f97316]" : "hover:bg-slate-50"}`}
                        data-pending={isPolicyPending(p) ? "true" : "false"}
                        onDoubleClick={() => {
                          // Pass return_to so closing the policy dialog brings the agent back here
                          const returnTo = `/commissions/statement${window.location.search || ""}`;
                          navigate(`/policies?focus=${p.id}&return_to=${encodeURIComponent(returnTo)}`);
                        }}
                        title="Doppio click per aprire la polizza"
                        data-testid={`statement-row-${p.id}`}
                      >
                        <td className="py-2 text-xs font-mono">
                          <span className="inline-flex items-center gap-1">
                            {hasInst ? (
                              <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); setExpanded((s) => ({ ...s, [p.id]: !s[p.id] })); }}
                                className="text-slate-500 hover:text-indigo-700 transition-colors"
                                data-testid={`statement-expand-${p.id}`}
                                title={isOpen ? "Nascondi dettaglio rate" : `Mostra dettaglio ${p.installments_in_range.length} rate pagate nel periodo`}
                              >
                                {isOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                              </button>
                            ) : (
                              <span className="inline-block w-3.5" />
                            )}
                            {p.policy_number}
                            {hasInst && (
                              <span className="ml-1 text-[9px] px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-200 font-sans" title="Rate pagate nel periodo">
                                {p.installments_in_range.length} rate
                              </span>
                            )}
                            {p.paid_date_backfilled && (
                              <span
                                data-testid={`statement-backfill-badge-${p.id}`}
                                className="ml-1 text-[9px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-900 border border-amber-300 font-sans font-semibold inline-flex items-center gap-0.5"
                                title="Almeno una rata non ha una 'Data pagam. effettivo' compilata: l'Estratto usa come default il paid_date polizza. Aggiorna le date rate per un calcolo più preciso."
                              >
                                ⚠ backfill
                              </span>
                            )}
                          </span>
                        </td>
                        <td className="py-2">{p.client_name}</td>
                        <td className="py-2 capitalize text-xs">{(p.type || "").replace(/_/g, " ")}</td>
                        <td className="py-2 text-xs tabular-nums text-emerald-800" title={hasInst ? `${p.installments_in_range.length} rate pagate nel periodo` : ""}>
                          {hasInst ? (
                            p.installments_in_range.length === 1
                              ? formatDate(p.installments_in_range[0].paid_date)
                              : `${formatDate(p.installments_in_range[0].paid_date)} +${p.installments_in_range.length - 1}`
                          ) : (
                            p.policy_paid_date ? formatDate(p.policy_paid_date) : <span className="text-slate-300">—</span>
                          )}
                        </td>
                        <td className="py-2 text-xs text-slate-600">
                          {groupBy === "company"
                            ? (p.broker_name || <span className="text-slate-400">—</span>)
                            : (p.company_name || <span className="text-slate-400">—</span>)}
                        </td>
                        <td className="py-2 text-right tabular-nums">{formatCurrency(p.premium)}</td>
                        <td className="py-2 text-right tabular-nums text-slate-600">
                          {p.premium_net > 0 ? formatCurrency(p.premium_net) : <span className="text-slate-300">—</span>}
                        </td>
                        {/* Fee Broker (rip.) tot. */}
                        <td className="py-2 text-right tabular-nums text-sky-700" title="Fee Broker (rip.) — importo pattuito da ripartire secondo la % accordata">
                          {(p.cost ?? 0) > 0 ? formatCurrency(p.cost) : <span className="text-slate-300">—</span>}
                        </td>
                        {/* Spese Gest. tot. */}
                        <td className="py-2 text-right tabular-nums text-rose-700" title={p.broker_management_fee > 0 ? "Spese di gestione broker/agenzia — 100% al broker" : ""}>
                          {p.broker_management_fee > 0 ? formatCurrency(p.broker_management_fee) : <span className="text-slate-300">—</span>}
                        </td>
                        {/* Comp. Fisso tot. */}
                        <td className="py-2 text-right tabular-nums text-purple-700" title={p.broker_fixed_fee > 0 ? "Compenso fisso pattuito col broker/agenzia" : ""}>
                          {p.broker_fixed_fee > 0 ? formatCurrency(p.broker_fixed_fee) : <span className="text-slate-300">—</span>}
                        </td>
                        {/* Extra Fees tot. */}
                        <td className="py-2 text-right tabular-nums text-amber-700">
                          {p.extra_fees > 0 ? formatCurrency(p.extra_fees) : <span className="text-slate-300">—</span>}
                        </td>
                        {/* Tot. Fee Agenzia = quota agenzia sulla Fee Broker (rip.) */}
                        <td className="py-2 text-right tabular-nums text-indigo-700" title={p.cost > 0 ? `Quota agenzia sulla Fee Broker = ${(p.fee_agency_share_percent ?? 100).toFixed(1)}% × ${formatCurrency(p.cost)}` : "Nessuna Fee Broker → 0"}>
                          {(p.agency_from_cost ?? 0) > 0
                            ? <>{formatCurrency(p.agency_from_cost)} <span className="text-[10px] text-slate-400">({(p.fee_agency_share_percent ?? 100).toFixed(1)}%)</span></>
                            : <span className="text-slate-300">—</span>}
                        </td>
                        {/* Margine (auto) */}
                        <td className="py-2 text-right tabular-nums text-emerald-700" title="Margine (auto) = Prezzo vendita − Prezzo acquisto prodotti">
                          {(p.margin_auto ?? 0) !== 0 ? formatCurrency(p.margin_auto) : <span className="text-slate-300">—</span>}
                        </td>
                        {/* 🆕 Feb 2026 — Multi-Garanzia */}
                        <td
                          className="py-2 text-right tabular-nums"
                          title={(p.multi_guarantee_bonus_amount ?? 0) > 0
                            ? `Bonus Multi-Garanzia attivo: la provvigione standard è stata sostituita da ${formatCurrency(p.multi_guarantee_bonus_amount)}.`
                            : "Bonus Multi-Garanzia non attivo per questa polizza."}
                          data-testid={`multi-guarantee-cell-${p.id}`}
                        >
                          {(p.multi_guarantee_bonus_amount ?? 0) > 0 ? (
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-900 border border-amber-300 text-xs font-semibold">
                              ⭐ {formatCurrency(p.multi_guarantee_bonus_amount)}
                            </span>
                          ) : (
                            <span className="text-slate-300">—</span>
                          )}
                        </td>
                        {/* Provv. Ag. (fee incl.) — quota totale agenzia + badge di parity con dettaglio polizza */}
                        <td className="py-2 text-right tabular-nums text-indigo-700 font-semibold" title="Provv. Ag. (fee incl.) = Provvigione Agenzia + Fee Broker (rip.) × %agenzia + Extra + Comp. Fisso">
                          {(() => {
                            if (p.agency_total == null) {
                              return p.agency_net != null ? formatCurrency(p.agency_net) : <span className="text-slate-300">—</span>;
                            }
                            // 🆕 20/07/2026 — Badge parity: confronta agency_total col calcolo editor (commission × share%) + (cost × share%) + extra_raw + fixed_raw.
                            const sh = Number(p.agency_share_percent || 0);
                            // Per il calcolo di parity usiamo i valori mostrati nella riga (post-prorata) per essere coerenti visivamente.
                            const commissionShare = Number(p.commission || 0) * sh / 100;
                            const costShare = Number(p.cost || 0) * Number(p.fee_agency_share_percent ?? sh) / 100;
                            const extraRaw = Number(p.extra_fees_raw ?? p.extra_fees ?? 0);
                            const fixedRaw = Number(p.broker_fixed_fee_raw ?? p.broker_fixed_fee ?? 0);
                            const editorExpectedFull = commissionShare + costShare + extraRaw + fixedRaw;
                            const excludeExtra = p.exclude_extra_fees_from_statement === true;
                            const excludeFixed = p.exclude_broker_fixed_from_statement === true;
                            const editorExpectedApplied = commissionShare + costShare
                              + (excludeExtra ? 0 : extraRaw)
                              + (excludeFixed ? 0 : fixedRaw);
                            const actual = Number(p.agency_total);
                            const eps = 0.02;
                            const inParity = Math.abs(actual - editorExpectedApplied) <= eps;
                            const excludedTags = [];
                            if (excludeExtra) excludedTags.push(`Extra ${formatCurrency(extraRaw)}`);
                            if (excludeFixed) excludedTags.push(`C. Fisso ${formatCurrency(fixedRaw)}`);
                            let badge;
                            if (!inParity) {
                              badge = (
                                <span className="ml-1 inline-block align-middle text-[9px] font-semibold text-red-700 bg-red-50 border border-red-200 rounded px-1 py-[1px] leading-none" title={`⚠️ Discrepanza: atteso ${formatCurrency(editorExpectedApplied)}, mostrato ${formatCurrency(actual)}.`} data-testid={`parity-badge-${p.id}`}>⚠</span>
                              );
                            } else if (excludedTags.length > 0) {
                              badge = (
                                <span className="ml-1 inline-block align-middle text-[9px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded px-1 py-[1px] leading-none" title={`Esclusi dall'Estratto Conto: ${excludedTags.join(", ")}. Nel dettaglio polizza il totale sarebbe ${formatCurrency(editorExpectedFull)}.`} data-testid={`parity-badge-${p.id}`}>🚫 {excludedTags.length}</span>
                              );
                            } else {
                              badge = (
                                <span className="ml-1 inline-block align-middle text-[9px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1 py-[1px] leading-none" title={`Totale coerente col dettaglio polizza (${formatCurrency(editorExpectedFull)}).`} data-testid={`parity-badge-${p.id}`}>✓</span>
                              );
                            }
                            return (
                              <span className="inline-flex items-center justify-end gap-0.5">
                                {formatCurrency(actual)}
                                {badge}
                              </span>
                            );
                          })()}
                        </td>
                        <td className="py-2 text-center" data-testid={`collect-status-${p.id}`}>
                          {(() => {
                            const inst = p.installments_in_range || [];
                            const total = inst.length;
                            if (total === 0) return <span className="text-slate-300">—</span>;
                            const collected = inst.filter((r) => r.commission_collection).length;
                            const uncollected = total - collected;
                            const totalCollectedAmt = inst.reduce((s, r) => s + (r.commission_collection?.amount_collected || 0), 0);
                            let bgCls = "bg-red-100 text-red-800 border-red-300";
                            let icon = "○";
                            if (collected === total && total > 0) { bgCls = "bg-emerald-100 text-emerald-800 border-emerald-300"; icon = "●"; }
                            else if (collected > 0) { bgCls = "bg-amber-100 text-amber-900 border-amber-300"; icon = "◐"; }
                            const tooltip = collected === total
                              ? `Tutte incassate · ${formatCurrency(totalCollectedAmt)}`
                              : collected > 0
                                ? `${collected}/${total} incassate · ${formatCurrency(totalCollectedAmt)} incassato`
                                : `Nessuna delle ${total} rate ancora incassata`;
                            const allUncollectedSelected = uncollected > 0 && inst.filter((r) => !r.commission_collection).every((r) => selectedInstallments[`${p.id}::${r.due_date}`]);
                            return (
                              <div className="inline-flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                                {/* 🆕 20/07/2026 — Row checkbox: seleziona TUTTE le rate rimanenti per bulk */}
                                {uncollected > 0 && (
                                  <input
                                    type="checkbox"
                                    className="h-3.5 w-3.5 cursor-pointer accent-emerald-600"
                                    checked={allUncollectedSelected}
                                    onChange={() => toggleRowSelection(p)}
                                    title={`Seleziona ${uncollected} rat${uncollected === 1 ? "a rimanente" : "e rimanenti"} per incasso multiplo`}
                                    data-testid={`row-select-${p.id}`}
                                  />
                                )}
                                <span
                                  className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[10px] font-semibold tabular-nums ${bgCls}`}
                                  title={tooltip}
                                >
                                  {icon} {collected}/{total}
                                </span>
                                {/* 🆕 20/07/2026 — Quick Incasso "1-click": marca tutte le rate rimanenti come incassate oggi */}
                                {uncollected > 0 && (
                                  <button
                                    type="button"
                                    onClick={() => quickCollectPolicy(p)}
                                    className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white transition-colors shadow-sm"
                                    title={`Incassa oggi le ${uncollected} rat${uncollected === 1 ? "a rimanente" : "e rimanenti"} (data odierna)`}
                                    data-testid={`quick-collect-${p.id}`}
                                  >
                                    <CheckCircle2 className="h-3 w-3" />
                                  </button>
                                )}
                              </div>
                            );
                          })()}
                        </td>
                      </tr>
                      {hasInst && (isOpen || (exporting && p.installments_total > 1)) && (
                        <tr className="bg-emerald-50/30 border-b border-emerald-100" data-testid={`statement-installments-${p.id}`}>
                          <td colSpan={16} className="py-3 px-4">
                            <div className="text-[11px] uppercase tracking-wide text-emerald-800 mb-1.5 flex items-center gap-2">
                              <span>Dettaglio rate pagate nel periodo</span>
                              {p.commission_percent > 0 && (
                                <span className="text-[10px] text-slate-500 normal-case font-normal">
                                  · aliquota provvigione {p.commission_percent.toFixed(2)}%
                                  {p.taxable_ratio > 0 && <> · ratio imponibile/premio {(p.taxable_ratio * 100).toFixed(1)}%</>}
                                </span>
                              )}
                            </div>
                            <table className="w-full text-xs bg-white border border-emerald-200 rounded">
                              <thead className="text-[10px] uppercase tracking-wide text-emerald-800 bg-emerald-50">
                                <tr>
                                  <th className="text-center py-1.5 px-2 w-8" title="Seleziona per bulk">
                                    <input
                                      type="checkbox"
                                      className="h-3.5 w-3.5 cursor-pointer"
                                      data-testid={`inst-select-all-${p.id}`}
                                      onChange={(e) => {
                                        const check = e.target.checked;
                                        setSelectedInstallments((s) => {
                                          const next = { ...s };
                                          p.installments_in_range.forEach((r) => {
                                            const key = `${p.id}::${r.due_date}`;
                                            if (check && !r.commission_collection) next[key] = { policy_id: p.id, installment_date: r.due_date, paid_date: r.paid_date || null };
                                            else if (!check) delete next[key];
                                          });
                                          return next;
                                        });
                                      }}
                                      checked={p.installments_in_range.filter((r) => !r.commission_collection).every((r) => selectedInstallments[`${p.id}::${r.due_date}`]) && p.installments_in_range.some((r) => !r.commission_collection)}
                                    />
                                  </th>
                                  <th className="text-left py-1.5 px-2">Scadenza</th>
                                  <th className="text-left py-1.5 px-2">Data pagamento</th>
                                  <th className="text-right py-1.5 px-2">Importo rata</th>
                                  <th className="text-right py-1.5 px-2">Imponibile rata</th>
                                  <th className="text-right py-1.5 px-2">Provvigione</th>
                                  <th className="text-right py-1.5 px-2" title="Fee Broker della rata (fee annuale ÷ numero rate)">Fee</th>
                                  <th className="text-left py-1.5 px-2">Stato incasso</th>
                                  <th className="text-center py-1.5 px-2">Azioni</th>
                                </tr>
                              </thead>
                              <tbody>
                                {p.installments_in_range
                                  .filter((r) => {
                                    if (collectionStatusFilter === "collected") return !!r.commission_collection;
                                    if (collectionStatusFilter === "uncollected") return !r.commission_collection;
                                    return true;
                                  })
                                  .map((r, idx) => {
                                    const collected = r.commission_collection;
                                    const selKey = `${p.id}::${r.due_date}`;
                                    const isSelected = !!selectedInstallments[selKey];
                                    return (
                                    <tr key={`${p.id}-inst-${idx}`} className={`border-t border-emerald-100 ${isInstPending(r) ? "bg-yellow-50 shadow-[inset_4px_0_0_#f97316]" : "bg-emerald-50/40"}`} data-testid={`inst-row-${p.id}-${r.due_date}`} data-pending={isInstPending(r) ? "true" : "false"}>
                                      <td className="py-1 px-2 text-center">
                                        {!collected && (
                                          <input
                                            type="checkbox"
                                            className="h-3.5 w-3.5 cursor-pointer"
                                            data-testid={`inst-checkbox-${p.id}-${r.due_date}`}
                                            checked={isSelected}
                                            onChange={() => toggleInstallmentSelection(p.id, r.due_date, r.paid_date || null)}
                                          />
                                        )}
                                      </td>
                                      <td className="py-1 px-2 tabular-nums text-slate-700">{formatDate(r.due_date)}</td>
                                      <td className="py-1 px-2 tabular-nums text-emerald-800 font-medium">{formatDate(r.paid_date)}</td>
                                      <td className="py-1 px-2 text-right tabular-nums">{formatCurrency(r.amount || 0)}</td>
                                      <td className="py-1 px-2 text-right tabular-nums text-slate-700">{formatCurrency(r.taxable_base || 0)}</td>
                                      <td className="py-1 px-2 text-right tabular-nums text-emerald-800 font-semibold">{formatCurrency(r.commission || 0)}</td>
                                      <td className="py-1 px-2 text-right tabular-nums text-indigo-700">
                                        <div className="flex items-center justify-end gap-1.5">
                                          <span data-testid={`inst-fee-${p.id}-${r.due_date}`}>{formatCurrency(r.fee || 0)}</span>
                                          {r.fee > 0 && (
                                            <button
                                              type="button"
                                              onClick={() => toggleFeeCollection(p.id, r)}
                                              className={`text-[9px] font-semibold px-1.5 py-0.5 rounded border transition-colors ${r.fee_collection ? "bg-indigo-100 text-indigo-800 border-indigo-300 hover:bg-indigo-200" : "bg-amber-50 text-amber-900 border-amber-300 hover:bg-amber-100"}`}
                                              title={r.fee_collection ? `Fee incassata ${formatDate(r.fee_collection.collected_at)} · ${formatCurrency(r.fee_collection.amount_collected || 0)} — clic per annullare` : "Clic per segnare la fee come incassata"}
                                              data-testid={`inst-fee-toggle-${p.id}-${r.due_date}`}
                                            >
                                              {r.fee_collection ? `Incassata ${formatDate(r.fee_collection.collected_at)}` : "Da incassare"}
                                            </button>
                                          )}
                                        </div>
                                      </td>
                                      <td className="py-1 px-2">
                                        {collected ? (
                                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 border border-emerald-300 text-[10px] font-semibold" title={`Incassato: ${formatCurrency(collected.amount_collected || 0)}${collected.note ? " · " + collected.note : ""}`}>
                                            <CheckCircle2 className="h-3 w-3" /> Incassata {formatDate(collected.collected_at)}
                                          </span>
                                        ) : (
                                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-100 text-amber-900 border border-amber-300 text-[10px] font-semibold">
                                            Da incassare
                                          </span>
                                        )}
                                      </td>
                                      <td className="py-1 px-2 text-center">
                                        {collected ? (
                                          <button
                                            type="button"
                                            data-testid={`inst-uncollect-${p.id}-${r.due_date}`}
                                            onClick={() => uncollectSingle(p.id, r.due_date)}
                                            className="text-red-600 hover:text-red-800 hover:bg-red-50 rounded p-1 transition-colors"
                                            title="Annulla incasso"
                                          >
                                            <XCircle className="h-3.5 w-3.5" />
                                          </button>
                                        ) : (
                                          <button
                                            type="button"
                                            data-testid={`inst-collect-${p.id}-${r.due_date}`}
                                            onClick={() => {
                                              // amount default = quota agenzia (approx = commission * agency_share%)
                                              const share = (p.agency_share_percent ?? 100) / 100;
                                              const collabPct = (p.collab_share_percent || 0) / 100;
                                              const afterCollab = (r.commission || 0) * (1 - collabPct);
                                              const agencyPart = afterCollab * share;
                                              openSingleCollectDialog(p.id, r.due_date, agencyPart, null);
                                            }}
                                            className="text-emerald-600 hover:text-emerald-800 hover:bg-emerald-50 rounded p-1 transition-colors"
                                            title="Segna come incassata"
                                          >
                                            <CheckCircle2 className="h-3.5 w-3.5" />
                                          </button>
                                        )}
                                      </td>
                                    </tr>
                                  );
                                })}
                                <tr className="bg-emerald-50/70 border-t border-emerald-200 font-semibold">
                                  <td colSpan="3" className="py-1.5 px-2 text-[10px] uppercase tracking-wide text-emerald-900">
                                    Totale ({p.installments_in_range.length} rate)
                                  </td>
                                  <td className="py-1.5 px-2 text-right tabular-nums">
                                    {formatCurrency(p.installments_in_range.reduce((s, r) => s + (r.amount || 0), 0))}
                                  </td>
                                  <td className="py-1.5 px-2 text-right tabular-nums text-slate-800">
                                    {formatCurrency(p.installments_in_range.reduce((s, r) => s + (r.taxable_base || 0), 0))}
                                  </td>
                                  <td className="py-1.5 px-2 text-right tabular-nums text-emerald-900">
                                    {formatCurrency(p.installments_in_range.reduce((s, r) => s + (r.commission || 0), 0))}
                                  </td>
                                  <td className="py-1.5 px-2 text-right tabular-nums text-indigo-800" data-testid={`inst-fee-total-${p.id}`}>
                                    {formatCurrency(p.installments_in_range.reduce((s, r) => s + (r.fee || 0), 0))}
                                  </td>
                                  <td colSpan="2" />
                                </tr>
                              </tbody>
                            </table>
                          </td>
                        </tr>
                      )}
                      </Fragment>
                      );
                    })}
                    <tr className="font-medium bg-slate-50">
                      <td colSpan="5" className="py-2 text-xs uppercase tracking-wide text-slate-600">Totale {g.name}</td>
                      <td className="py-2 text-right tabular-nums">{formatCurrency(g.totals.premium)}</td>
                      <td className="py-2 text-right tabular-nums text-slate-700">{formatCurrency(g.totals.premium_net || 0)}</td>
                      <td className="py-2 text-right tabular-nums text-sky-700">{formatCurrency(g.totals.cost)}</td>
                      <td className="py-2 text-right tabular-nums text-rose-700">{formatCurrency(g.totals.broker_management_fee || 0)}</td>
                      <td className="py-2 text-right tabular-nums text-purple-700">{formatCurrency(g.totals.broker_fixed_fee || 0)}</td>
                      <td className="py-2 text-right tabular-nums text-amber-700">{formatCurrency(g.totals.extra_fees || 0)}</td>
                      <td className="py-2 text-right tabular-nums text-indigo-700">{formatCurrency(g.totals.agency_from_cost || 0)}</td>
                      <td className="py-2 text-right tabular-nums text-emerald-700">{formatCurrency((g.policies || []).reduce((s, p) => s + (p.margin_auto || 0), 0))}</td>
                      <td className="py-2 text-right tabular-nums text-amber-800 font-semibold">
                        {(() => {
                          const tot = (g.policies || []).reduce((s, p) => s + (p.multi_guarantee_bonus_amount || 0), 0);
                          return tot > 0 ? formatCurrency(tot) : <span className="text-slate-300">—</span>;
                        })()}
                      </td>
                      <td className="py-2 text-right tabular-nums text-indigo-800 font-semibold">{formatCurrency(g.totals.agency_total || g.totals.agency_net || 0)}</td>
                      <td className="py-2 text-center">
                        {(() => {
                          let totInst = 0, totCollected = 0, totAmt = 0;
                          (g.policies || []).forEach((p) => {
                            (p.installments_in_range || []).forEach((r) => {
                              totInst++;
                              if (r.commission_collection) { totCollected++; totAmt += r.commission_collection.amount_collected || 0; }
                            });
                          });
                          if (totInst === 0) return <span className="text-slate-300">—</span>;
                          const bgCls = totCollected === totInst ? "text-emerald-800" : totCollected > 0 ? "text-amber-800" : "text-red-700";
                          return (
                            <span className={`text-[11px] font-bold tabular-nums ${bgCls}`} title={`${totCollected}/${totInst} incassate · ${formatCurrency(totAmt)}`}>
                              {totCollected}/{totInst}
                            </span>
                          );
                        })()}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              );
            })}

            {data.groups.length > 1 && (
              <div className="mb-6" data-testid="grand-total-table">
                <div className="border-b-2 border-slate-900 pb-2 mb-3">
                  <h3 className="font-display text-lg font-semibold text-slate-900">Totale generale periodo</h3>
                </div>
                <table className="w-full text-sm">
                  <tbody>
                    <tr className="font-semibold bg-indigo-50 border-t-2 border-indigo-200">
                      <td colSpan="5" className="py-2 text-xs uppercase tracking-wide text-indigo-800">TOTALE GENERALE ({data.totals.count} polizze)</td>
                      <td className="py-2 text-right tabular-nums" data-testid="grand-premium">{formatCurrency(data.totals.premium)}</td>
                      <td className="py-2 text-right tabular-nums text-slate-700" data-testid="grand-premium-net">{formatCurrency(data.totals.premium_net || 0)}</td>
                      <td className="py-2 text-right tabular-nums text-sky-700" data-testid="grand-cost">{formatCurrency(data.totals.cost)}</td>
                      <td className="py-2 text-right tabular-nums text-rose-700" data-testid="grand-mgmt-fee">{formatCurrency(data.totals.broker_management_fee || 0)}</td>
                      <td className="py-2 text-right tabular-nums text-purple-700" data-testid="grand-broker-fixed">{formatCurrency(data.totals.broker_fixed_fee || 0)}</td>
                      <td className="py-2 text-right tabular-nums text-amber-700" data-testid="grand-extra">{formatCurrency(data.totals.extra_fees || 0)}</td>
                      <td className="py-2 text-right tabular-nums text-indigo-700" data-testid="grand-agency-fee">{formatCurrency(data.totals.agency_from_cost || 0)}</td>
                      <td className="py-2 text-right tabular-nums text-emerald-700" data-testid="grand-margin-auto">{formatCurrency((data.groups || []).reduce((s, g) => s + (g.policies || []).reduce((s2, p) => s2 + (p.margin_auto || 0), 0), 0))}</td>
                      <td className="py-2 text-right tabular-nums text-amber-800 font-semibold" data-testid="grand-multi-guarantee">
                        {(() => {
                          const tot = (data.groups || []).reduce((s, g) => s + (g.policies || []).reduce((s2, p) => s2 + (p.multi_guarantee_bonus_amount || 0), 0), 0);
                          return tot > 0 ? formatCurrency(tot) : <span className="text-slate-300">—</span>;
                        })()}
                      </td>
                      <td className="py-2 text-right tabular-nums text-indigo-900 font-bold" data-testid="grand-agency-total">{formatCurrency(data.totals.agency_total || data.totals.agency_net || 0)}</td>
                      <td className="py-2 text-center" data-testid="grand-collect-status">
                        {(() => {
                          let totInst = 0, totCollected = 0, totAmt = 0;
                          (data.groups || []).forEach((g) => (g.policies || []).forEach((p) => {
                            (p.installments_in_range || []).forEach((r) => {
                              totInst++;
                              if (r.commission_collection) { totCollected++; totAmt += r.commission_collection.amount_collected || 0; }
                            });
                          }));
                          if (totInst === 0) return <span className="text-slate-300">—</span>;
                          const bgCls = totCollected === totInst ? "bg-emerald-100 text-emerald-900 border-emerald-400" : totCollected > 0 ? "bg-amber-100 text-amber-900 border-amber-400" : "bg-red-100 text-red-800 border-red-400";
                          return (
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border text-[11px] font-bold tabular-nums ${bgCls}`} title={`${totCollected}/${totInst} incassate · ${formatCurrency(totAmt)}`}>
                              {totCollected}/{totInst}
                            </span>
                          );
                        })()}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}

            <div className="border-t-2 border-slate-900 pt-4 mt-8 flex justify-between items-end">
              <p className="text-xs text-slate-500">
                Documento generato automaticamente da {agency?.name || "Assicura"}. Valori non vincolanti, soggetti a verifica contabile.
              </p>
              <div className="text-right">
                <p className="text-[10px] uppercase tracking-wide text-slate-500">Netto pool periodo</p>
                <p className="text-2xl font-display font-bold tabular-nums text-blue-900" data-testid="grand-net">{formatCurrency(data.totals.net)}</p>
                {(data.totals.agency_total || data.totals.agency_net) > 0 && (
                  <>
                    <p className="text-[10px] uppercase tracking-wide text-indigo-700 mt-2">Quota agenzia totale</p>
                    <p className="text-3xl font-display font-bold tabular-nums text-indigo-800" data-testid="grand-agency-net">{formatCurrency(data.totals.agency_total || data.totals.agency_net)}</p>
                  </>
                )}
              </div>
            </div>
          </>
        )}
      </div>

      <style>{`
        @media print {
          body { background: white; }
          aside, nav, header.print\\:hidden, .print\\:hidden { display: none !important; }
          main { padding: 0 !important; }
          /* 🆕 Su stampa nascondi sempre le colonne dettaglio Diritti (troppo strette su A4) */
          .col-detail { display: none !important; }
        }
        /* Applicata SOLO durante export PDF via html2pdf: compatta ma leggibile */
        .pdf-compact { padding: 14px !important; font-size: 10px !important; line-height: 1.25 !important; }
        .pdf-compact h2 { font-size: 14px !important; line-height: 1.2 !important; }
        .pdf-compact h3 { font-size: 12px !important; line-height: 1.2 !important; }
        .pdf-compact p { font-size: 9.5px !important; line-height: 1.25 !important; margin: 0 !important; }
        .pdf-compact th, .pdf-compact td { font-size: 9px !important; padding: 3px 4px !important; line-height: 1.2 !important; vertical-align: middle !important; }
        .pdf-compact th { font-size: 8.5px !important; }
        .pdf-compact table { font-size: 9px !important; border-collapse: collapse !important; width: 100% !important; }
        .pdf-compact .tabular-nums { font-size: 9px !important; }
        .pdf-compact .text-\\[9px\\], .pdf-compact .text-\\[10px\\], .pdf-compact .text-\\[11px\\] { font-size: 7.5px !important; }
        .pdf-compact .text-xs { font-size: 8px !important; }
        .pdf-compact .text-sm { font-size: 9px !important; }
        .pdf-compact .text-base { font-size: 10px !important; }
        .pdf-compact .text-lg { font-size: 11px !important; }
        .pdf-compact .text-xl { font-size: 12px !important; }
        .pdf-compact .text-2xl { font-size: 14px !important; }
        .pdf-compact .text-3xl { font-size: 16px !important; }
        .pdf-compact .p-3 { padding: 6px !important; }
        .pdf-compact .p-8 { padding: 12px !important; }
        .pdf-compact .py-2 { padding-top: 3px !important; padding-bottom: 3px !important; }
        .pdf-compact .mb-6 { margin-bottom: 10px !important; }
        .pdf-compact .mb-8 { margin-bottom: 12px !important; }
        .pdf-compact .mb-3 { margin-bottom: 6px !important; }
        .pdf-compact .mt-8 { margin-top: 12px !important; }
        .pdf-compact .gap-3 { gap: 6px !important; }
        .pdf-compact .h-12 { height: 36px !important; }
        .pdf-compact .w-12 { width: 36px !important; }
        /* Impedisce sovrapposizioni: forza block/valign nelle celle */
        .pdf-compact td, .pdf-compact th { white-space: normal !important; word-break: break-word !important; }
        /* 🆕 Forza numeri sempre su una riga (evita line-break interno che sovrappone i testi) */
        .pdf-compact .tabular-nums, .pdf-dense .tabular-nums { white-space: nowrap !important; }
        /* Le celle numeriche non spezzano parole */
        .pdf-compact td[class*="text-right"], .pdf-dense td[class*="text-right"],
        .pdf-compact th[class*="text-right"], .pdf-dense th[class*="text-right"] {
          white-space: nowrap !important; word-break: keep-all !important;
        }
        /* KPI cards: forza layout verticale (label sopra, valore sotto) senza flex */
        .pdf-compact .grid > div, .pdf-dense .grid > div { display: block !important; }
        .pdf-compact .grid > div > p, .pdf-dense .grid > div > p { display: block !important; }
        /* Footer riepilogo: evita che il testo di sinistra e i totali di destra si sovrappongano */
        .pdf-compact .flex.justify-between, .pdf-dense .flex.justify-between { align-items: flex-start !important; gap: 12px !important; }
        /* 🆕 Layout DENSO per tabelle con molte colonne (>=10) — attivato dinamicamente da exportPdf */
        .pdf-dense { padding: 10px !important; font-size: 8.5px !important; line-height: 1.35 !important; }
        .pdf-dense h3 { font-size: 10.5px !important; line-height: 1.25 !important; }
        .pdf-dense p { line-height: 1.3 !important; margin: 0 !important; }
        .pdf-dense th, .pdf-dense td { font-size: 7.5px !important; padding: 3px 3px !important; line-height: 1.3 !important; vertical-align: middle !important; }
        .pdf-dense th { font-size: 7px !important; letter-spacing: 0 !important; text-transform: none !important; }
        .pdf-dense table { font-size: 7.5px !important; table-layout: auto !important; border-collapse: collapse !important; width: 100% !important; }
        .pdf-dense .tabular-nums { font-size: 7.5px !important; }
        .pdf-dense .text-\\[9px\\], .pdf-dense .text-\\[10px\\], .pdf-dense .text-\\[11px\\] { font-size: 6.5px !important; line-height: 1.3 !important; }
        .pdf-dense .text-xs { font-size: 6.5px !important; line-height: 1.3 !important; }
        .pdf-dense .text-sm { font-size: 7.5px !important; line-height: 1.3 !important; }
        .pdf-dense .text-lg { font-size: 9.5px !important; line-height: 1.25 !important; }
        .pdf-dense .text-xl { font-size: 10.5px !important; line-height: 1.25 !important; }
        .pdf-dense .text-2xl { font-size: 12px !important; line-height: 1.25 !important; }
        .pdf-dense .text-3xl { font-size: 13.5px !important; line-height: 1.25 !important; }
        .pdf-dense .p-3 { padding: 5px !important; }
        .pdf-dense .p-8 { padding: 8px !important; }
        .pdf-dense .py-2 { padding-top: 3px !important; padding-bottom: 3px !important; }
        .pdf-dense .mb-6 { margin-bottom: 6px !important; }
        .pdf-dense .mb-8 { margin-bottom: 8px !important; }
        .pdf-dense .mb-3 { margin-bottom: 4px !important; }
        .pdf-dense .mt-8 { margin-top: 8px !important; }
        .pdf-dense .gap-3 { gap: 4px !important; }
      `}</style>

      {/* 🆕 Dialog: segna singola provvigione come incassata */}
      <Dialog open={!!singleCollectDialog} onOpenChange={(o) => !o && setSingleCollectDialog(null)}>
        <DialogContent data-testid="single-collect-dialog">
          <DialogHeader>
            <DialogTitle>Segna provvigione come incassata</DialogTitle>
          </DialogHeader>
          {singleCollectDialog && (
            <div className="space-y-3 text-sm">
              <div className="p-2.5 rounded-md bg-slate-50 border border-slate-200">
                <p className="text-[11px] uppercase tracking-wide text-slate-500">Rata scadenza</p>
                <p className="text-sm font-semibold tabular-nums">{formatDate(singleCollectDialog.installment_date)}</p>
                <p className="text-[10px] text-slate-500 mt-1">Polizza: <span className="font-mono">{singleCollectDialog.policy_id.slice(0, 8)}…</span></p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Data incasso</Label>
                  <DatePicker value={singleCollectedAt} onChange={(e) => setSingleCollectedAt(e?.target?.value || "")} data-testid="single-collected-at" />
                </div>
                <div>
                  <Label className="text-xs">Importo agenzia (€)</Label>
                  <Input
                    data-testid="single-amount"
                    type="number"
                    step="0.01"
                    value={singleAmount}
                    onChange={(e) => setSingleAmount(e.target.value)}
                    className="h-8 tabular-nums"
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs">Note (opzionale)</Label>
                <Input
                  data-testid="single-note"
                  value={singleNote}
                  onChange={(e) => setSingleNote(e.target.value)}
                  placeholder="Es. bonifico ricevuto il..."
                  className="h-8"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSingleCollectDialog(null)} disabled={singleBusy} data-testid="single-cancel">Annulla</Button>
            <Button onClick={confirmSingleCollect} disabled={singleBusy} className="bg-emerald-600 hover:bg-emerald-700" data-testid="single-confirm">
              {singleBusy ? "Salvo…" : "Segna incassata"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 🆕 Dialog: conferma bulk */}
      <Dialog open={bulkDialogOpen} onOpenChange={setBulkDialogOpen}>
        <DialogContent data-testid="bulk-collect-dialog">
          <DialogHeader>
            <DialogTitle>Segna {selectedItems.length} provvigioni come incassate</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <p className="text-slate-600">
              Confermi di voler segnare come incassate <strong>{selectedItems.length}</strong> {selectedItems.length === 1 ? "rata" : "rate"}?
              L&apos;importo di ognuna sarà uguale alla quota agenzia calcolata (potrai modificare le singole successivamente).
            </p>
            <div>
              <Label className="text-xs">Data incasso comune</Label>
              <DatePicker value={bulkCollectedAt} onChange={(e) => setBulkCollectedAt(e?.target?.value || "")} data-testid="bulk-collected-at" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkDialogOpen(false)} disabled={bulkBusy} data-testid="bulk-cancel">Annulla</Button>
            <Button onClick={confirmBulkCollect} disabled={bulkBusy} className="bg-emerald-600 hover:bg-emerald-700" data-testid="bulk-confirm">
              {bulkBusy ? "Salvo…" : `Conferma (${selectedItems.length})`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* 🆕 20/07/2026 — Dialog "Incassa tutto il periodo" */}
      <Dialog open={collectAllOpen} onOpenChange={setCollectAllOpen}>
        <DialogContent data-testid="collect-all-dialog">
          <DialogHeader>
            <DialogTitle>Incassa tutto il periodo</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <p className="text-slate-600">
              Stai per segnare come incassate <strong className="text-emerald-800">{allUncollectedItems.length} rat{allUncollectedItems.length === 1 ? "a" : "e"}</strong> attualmente non incassate nell&apos;intero periodo <strong>{formatDate(startDate)} → {formatDate(endDate)}</strong>.
            </p>
            <div className="rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-900">
              <strong>Nota:</strong> data incasso = oggi ({formatDate(iso(new Date()))}). L&apos;importo di ogni rata sarà uguale alla quota agenzia calcolata. Potrai modificare singolarmente in seguito.
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCollectAllOpen(false)} disabled={collectAllBusy} data-testid="collect-all-cancel">Annulla</Button>
            <Button onClick={confirmCollectAll} disabled={collectAllBusy || allUncollectedItems.length === 0} className="bg-emerald-600 hover:bg-emerald-700" data-testid="collect-all-confirm">
              {collectAllBusy ? "Incasso…" : `Incassa ${allUncollectedItems.length} rat${allUncollectedItems.length === 1 ? "a" : "e"}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
